<!--
  InteractiveGrid.svelte — Canvas-based interactive wireframe grid.

  Renders a grid of lines and intersection dots that warp around the
  cursor with smooth lerp interpolation. Ambient spotlights drift
  across the grid, fading in and out to add life without interaction.

  Used on the auth layout left panel to add visual depth and interactivity.
-->
<script lang="ts">
	import { onMount } from 'svelte';

	/** Grid cell size in CSS pixels. */
	const CELL_SIZE = 60;
	/** How far (px) the cursor displaces nearby grid points. */
	const INFLUENCE_RADIUS = 180;
	/** Maximum pixel displacement from cursor repulsion. */
	const MAX_DISPLACEMENT = 18;
	/** Lerp speed for grid point interpolation toward targets. */
	const POINT_LERP = 0.08;

	/* ── Spotlight configuration ─────────────────────────── */

	/** Maximum concurrent spotlights. */
	const MAX_SPOTLIGHTS = 3;
	/** Spotlight radius range in CSS pixels. */
	const SPOT_MIN_RADIUS = 120;
	const SPOT_MAX_RADIUS = 450;
	/** Spotlight lifetime range in frames. */
	const SPOT_MIN_LIFE = 200;
	const SPOT_MAX_LIFE = 500;
	/** Drift speed range in px/frame. */
	const SPOT_MIN_SPEED = 0.15;
	const SPOT_MAX_SPEED = 0.5;
	/** Interval between attempting to spawn a new spotlight (frames). */
	const SPOT_SPAWN_INTERVAL = 90;

	/** Canvas element reference. */
	let canvas: HTMLCanvasElement;

	onMount(() => {
		const ctx = canvas.getContext('2d')!;
		let width = 0;
		let height = 0;
		let cols = 0;
		let rows = 0;
		let animId: number;
		let frameCount = 0;

		/** Normalized mouse position (0-1). */
		let mouseX = 0.5;
		let mouseY = 0.5;
		/** Smoothed mouse position for interpolation. */
		let smoothMouseX = 0.5;
		let smoothMouseY = 0.5;
		/** Whether the mouse is currently over the canvas. */
		let mouseOver = false;

		/** Grid point with base position and current displaced position. */
		interface GridPoint {
			baseX: number;
			baseY: number;
			x: number;
			y: number;
		}

		/** An ambient spotlight that drifts across the grid. */
		interface Spotlight {
			/** Current center X. */
			x: number;
			/** Current center Y. */
			y: number;
			/** Drift velocity X in px/frame. */
			vx: number;
			/** Drift velocity Y in px/frame. */
			vy: number;
			/** Radius of the gradient. */
			radius: number;
			/** Remaining lifetime in frames. */
			life: number;
			/** Total lifetime for fade calculation. */
			maxLife: number;
			/** Current opacity (updated each frame, used for grid warp intensity). */
			alpha: number;
		}

		let points: GridPoint[] = [];
		let spotlights: Spotlight[] = [];

		/** Linearly interpolate between two values. */
		function lerp(a: number, b: number, t: number): number {
			return a + (b - a) * t;
		}

		/** Detect dark mode from the document root. */
		function isDark(): boolean {
			return document.documentElement.classList.contains('dark');
		}

		/** Resize canvas to match its CSS dimensions (retina-aware). */
		function resize() {
			const dpr = window.devicePixelRatio || 1;
			width = canvas.offsetWidth;
			height = canvas.offsetHeight;
			canvas.width = width * dpr;
			canvas.height = height * dpr;
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

			cols = Math.ceil(width / CELL_SIZE) + 2;
			rows = Math.ceil(height / CELL_SIZE) + 2;

			points = [];
			/* Rebuild the point grid with one entry per intersection. */
			for (let y = 0; y < rows; y++) {
				for (let x = 0; x < cols; x++) {
					const px = x * CELL_SIZE - CELL_SIZE;
					const py = y * CELL_SIZE - CELL_SIZE;
					points.push({ baseX: px, baseY: py, x: px, y: py });
				}
			}
		}

		/** Spawn an ambient spotlight at a random position. */
		function spawnSpotlight(): void {
			if (spotlights.length >= MAX_SPOTLIGHTS) return;

			/* Random angle for drift direction. */
			const angle = Math.random() * Math.PI * 2;
			const speed = SPOT_MIN_SPEED + Math.random() * (SPOT_MAX_SPEED - SPOT_MIN_SPEED);
			const life = SPOT_MIN_LIFE + Math.floor(Math.random() * (SPOT_MAX_LIFE - SPOT_MIN_LIFE));

			spotlights.push({
				x: Math.random() * width,
				y: Math.random() * height,
				vx: Math.cos(angle) * speed,
				vy: Math.sin(angle) * speed,
				radius: SPOT_MIN_RADIUS + Math.random() * (SPOT_MAX_RADIUS - SPOT_MIN_RADIUS),
				life,
				maxLife: life,
				alpha: 0
			});
		}

		/** Step spotlight positions, compute alpha, and cull dead ones. */
		function updateSpotlights(): void {
			const alive: Spotlight[] = [];

			/* Move each spotlight and compute its current opacity. */
			for (const s of spotlights) {
				s.x += s.vx;
				s.y += s.vy;
				s.life--;

				/* Fade in during first 20%, fade out during last 20%. */
				const progress = 1 - s.life / s.maxLife;
				const fadeIn = Math.min(progress / 0.2, 1);
				const fadeOut = Math.min(s.life / (s.maxLife * 0.2), 1);
				s.alpha = fadeIn * fadeOut;

				/* Keep if still alive. */
				if (s.life > 0) {
					alive.push(s);
				}
			}

			spotlights = alive;
		}

		/** Draw all spotlight gradients. */
		function drawSpotlights(dark: boolean): void {
			/* Render each spotlight as a radial gradient. */
			for (const s of spotlights) {
				const grad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.radius);
				if (dark) {
					grad.addColorStop(0, `rgba(100, 160, 255, ${s.alpha * 0.07})`);
				} else {
					grad.addColorStop(0, `rgba(255, 255, 255, ${s.alpha * 0.12})`);
				}
				grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
				ctx.fillStyle = grad;
				ctx.fillRect(s.x - s.radius, s.y - s.radius, s.radius * 2, s.radius * 2);
			}
		}

		/** Main render loop — warps grid points, draws grid, dots, spotlights, and cursor glow. */
		function animate() {
			frameCount++;

			/* Smooth mouse interpolation. */
			smoothMouseX = lerp(smoothMouseX, mouseX, 0.08);
			smoothMouseY = lerp(smoothMouseY, mouseY, 0.08);

			const cursorX = smoothMouseX * width;
			const cursorY = smoothMouseY * height;

			/* Spawn and step spotlights before grid update so alpha is current. */
			if (frameCount % SPOT_SPAWN_INTERVAL === 0) {
				spawnSpotlight();
			}
			updateSpotlights();

			/* Update grid point positions — cursor + spotlight repulsion + lerp back to base. */
			for (const p of points) {
				let targetX = p.baseX;
				let targetY = p.baseY;

				/* Repel points away from cursor. */
				if (mouseOver) {
					const dx = p.baseX - cursorX;
					const dy = p.baseY - cursorY;
					const dist = Math.sqrt(dx * dx + dy * dy);
					if (dist < INFLUENCE_RADIUS && dist > 0) {
						const force = (1 - dist / INFLUENCE_RADIUS) * MAX_DISPLACEMENT;
						targetX += (dx / dist) * force;
						targetY += (dy / dist) * force;
					}
				}

				/* Repel points away from each spotlight, scaled by its opacity. */
				for (const s of spotlights) {
					if (s.alpha <= 0) continue;
					const dx = p.baseX - s.x;
					const dy = p.baseY - s.y;
					const dist = Math.sqrt(dx * dx + dy * dy);
					if (dist < s.radius && dist > 0) {
						const force = (1 - dist / s.radius) * MAX_DISPLACEMENT * s.alpha;
						targetX += (dx / dist) * force;
						targetY += (dy / dist) * force;
					}
				}

				p.x = lerp(p.x, targetX, POINT_LERP);
				p.y = lerp(p.y, targetY, POINT_LERP);
			}

			ctx.clearRect(0, 0, width, height);

			/* Determine colors based on theme. */
			const dark = isDark();
			const lineColor = dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.15)';
			const dotColor = dark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.25)';
			const glowInner = dark ? 'rgba(100, 160, 255, 0.08)' : 'rgba(255, 255, 255, 0.10)';

			/* Draw grid lines. */
			ctx.strokeStyle = lineColor;
			ctx.lineWidth = 1;

			/* Horizontal lines. */
			for (let y = 0; y < rows; y++) {
				ctx.beginPath();
				for (let x = 0; x < cols; x++) {
					const p = points[y * cols + x];
					if (x === 0) ctx.moveTo(p.x, p.y);
					else ctx.lineTo(p.x, p.y);
				}
				ctx.stroke();
			}

			/* Vertical lines. */
			for (let x = 0; x < cols; x++) {
				ctx.beginPath();
				for (let y = 0; y < rows; y++) {
					const p = points[y * cols + x];
					if (y === 0) ctx.moveTo(p.x, p.y);
					else ctx.lineTo(p.x, p.y);
				}
				ctx.stroke();
			}

			ctx.fillStyle = dotColor;
			/* Render a dot at each grid intersection, scaling up near cursor. */
			for (const p of points) {
				let size = 1.5;
				if (mouseOver) {
					const dx = cursorX - p.x;
					const dy = cursorY - p.y;
					const dist = Math.sqrt(dx * dx + dy * dy);
					if (dist < INFLUENCE_RADIUS) {
						size = lerp(1.5, 3.5, 1 - dist / INFLUENCE_RADIUS);
					}
				}
				ctx.beginPath();
				ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
				ctx.fill();
			}

			/* Draw ambient spotlights. */
			drawSpotlights(dark);

			/* Radial glow following cursor. */
			if (mouseOver) {
				const gradient = ctx.createRadialGradient(cursorX, cursorY, 0, cursorX, cursorY, INFLUENCE_RADIUS);
				gradient.addColorStop(0, glowInner);
				gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
				ctx.fillStyle = gradient;
				ctx.fillRect(0, 0, width, height);
			}

			animId = requestAnimationFrame(animate);
		}

		/** Update normalized mouse coordinates from a mouse event. */
		function onMouseMove(e: MouseEvent) {
			const rect = canvas.getBoundingClientRect();
			mouseX = (e.clientX - rect.left) / rect.width;
			mouseY = (e.clientY - rect.top) / rect.height;
		}

		/** Mark the cursor as hovering over the canvas. */
		function onMouseEnter() {
			mouseOver = true;
		}

		/** Mark the cursor as having left the canvas. */
		function onMouseLeave() {
			mouseOver = false;
		}

		/** Update normalized mouse coordinates from a touch event. */
		function onTouchMove(e: TouchEvent) {
			const touch = e.touches[0];
			const rect = canvas.getBoundingClientRect();
			mouseX = (touch.clientX - rect.left) / rect.width;
			mouseY = (touch.clientY - rect.top) / rect.height;
			mouseOver = true;
		}

		canvas.addEventListener('mousemove', onMouseMove);
		canvas.addEventListener('mouseenter', onMouseEnter);
		canvas.addEventListener('mouseleave', onMouseLeave);
		canvas.addEventListener('touchmove', onTouchMove, { passive: true });

		resize();
		window.addEventListener('resize', resize);
		animId = requestAnimationFrame(animate);

		return () => {
			cancelAnimationFrame(animId);
			window.removeEventListener('resize', resize);
			canvas.removeEventListener('mousemove', onMouseMove);
			canvas.removeEventListener('mouseenter', onMouseEnter);
			canvas.removeEventListener('mouseleave', onMouseLeave);
			canvas.removeEventListener('touchmove', onTouchMove);
		};
	});
</script>

<canvas bind:this={canvas} class="absolute inset-0 h-full w-full"></canvas>
